import type { ReactNode } from "react";

type IconBaseProps = {
  children: ReactNode;
  className?: string;
  size?: number;
  strokeWidth?: number;
  fill?: string;
};

type IconProps = Omit<IconBaseProps, "children">;

const IconBase = ({
  children,
  className,
  size = 20,
  strokeWidth = 2,
  fill = "none",
}: IconBaseProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const IconArrowRight = (props: IconProps) => (
  <IconBase {...props}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </IconBase>
);

export const IconArrowLeft = (props: IconProps) => (
  <IconBase {...props}>
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </IconBase>
);

export const IconArrowDown = (props: IconProps) => (
  <IconBase {...props}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <polyline points="5 12 12 19 19 12" />
  </IconBase>
);

export const IconStar = (props: IconProps) => (
  <IconBase {...props} fill="currentColor" strokeWidth={1.5}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </IconBase>
);

export const IconCheck = (props: IconProps) => (
  <IconBase {...props}>
    <polyline points="20 6 9 17 4 12" />
  </IconBase>
);

export const IconUser = (props: IconProps) => (
  <IconBase {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </IconBase>
);

export const IconHeart = (props: IconProps) => (
  <IconBase {...props}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </IconBase>
);

export const IconHeartFilled = (props: IconProps) => (
  <IconBase {...props} fill="currentColor" strokeWidth={1.5}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </IconBase>
);

export const IconClipboard = (props: IconProps) => (
  <IconBase {...props}>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </IconBase>
);

export const IconPillarPersonality = ({
  className,
  size = 40,
}: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 57 63"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M16.5463 5.64198C20.4984 3.02438 25.2378 1.5 30.3332 1.5C44.1403 1.5 55.3332 12.6929 55.3332 26.5C55.3332 36.2313 53.4809 45.5298 50.1079 54.0616M9.47519 12.7131C6.85759 16.6652 5.33321 21.4046 5.33321 26.5C5.33321 31.3922 3.92903 35.9562 1.5002 39.8104M8.12951 51.6733C14.1603 45.0185 17.8332 36.1883 17.8332 26.5C17.8332 19.5964 23.4296 14 30.3332 14C37.2368 14 42.8332 19.5964 42.8332 26.5C42.8332 28.2564 42.7618 29.9958 42.6197 31.7155M30.3343 26.5C30.3343 38.9087 25.8141 50.2617 18.3304 59.0021M40.4431 43.681C38.6172 50.0811 35.7968 56.0618 32.1595 61.4454"
      stroke="#00A06D"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconPillarPersonalityDark = ({
  className,
  size = 40,
}: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M16.5113 8.90794C19.0007 7.25914 21.986 6.29895 25.1956 6.29895C33.8925 6.29895 40.9428 13.3492 40.9428 22.0462C40.9428 28.1758 39.776 34.0328 37.6514 39.4069M12.0573 13.3619C10.4085 15.8514 9.44834 18.8366 9.44834 22.0462C9.44834 25.1277 8.56386 28.0025 7.03397 30.4302M11.2097 37.9026C15.0084 33.7108 17.3219 28.1487 17.3219 22.0462C17.3219 17.6977 20.8471 14.1726 25.1956 14.1726C29.544 14.1726 33.0692 17.6977 33.0692 22.0462C33.0692 23.1525 33.0242 24.2481 32.9347 25.3313M25.1962 22.0462C25.1962 29.8623 22.349 37.0134 17.6351 42.5189M31.5637 32.8683C30.4136 36.8996 28.637 40.6668 26.3459 44.0579"
      stroke="#CFBCFF"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconPillarPreference = ({
  className,
  size = 40,
}: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M42.8325 17.007C42.8325 11.7889 38.426 7.55872 32.9904 7.55872C28.9264 7.55872 25.4376 9.92348 23.9358 13.2979C22.434 9.92348 18.9452 7.55872 14.8811 7.55872C9.44554 7.55872 5.03912 11.7889 5.03912 17.007C5.03912 32.1676 23.9358 42.2026 23.9358 42.2026C23.9358 42.2026 42.8325 32.1676 42.8325 17.007Z"
      stroke="#00A06D"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconPillarPreferenceDark = ({
  className,
  size = 40,
}: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M42.8324 17.0069C42.8324 11.7888 38.426 7.55859 32.9904 7.55859C28.9263 7.55859 25.4376 9.92336 23.9357 13.2978C22.4339 9.92336 18.9452 7.55859 14.8811 7.55859C9.44548 7.55859 5.03906 11.7888 5.03906 17.0069C5.03906 32.1674 23.9357 42.2025 23.9357 42.2025C23.9357 42.2025 42.8324 32.1674 42.8324 17.0069Z"
      stroke="#CFBCFF"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconPillarProject = ({ className, size = 40 }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M18.8967 25.1955H26.7703M18.8967 31.4944H26.7703M18.8967 37.7933H26.7703M33.0692 39.368H37.7934C40.4025 39.368 42.5175 37.2529 42.5175 34.6438V12.8249C42.5175 10.4419 40.7434 8.41979 38.3685 8.22268C37.5847 8.15763 36.7984 8.10155 36.0097 8.05454M23.8298 8.05454C23.694 8.49509 23.6209 8.96315 23.6209 9.44829C23.6209 10.318 24.3259 11.023 25.1956 11.023H34.6439C35.5136 11.023 36.2187 10.318 36.2187 9.44829C36.2187 8.96315 36.1455 8.49509 36.0097 8.05454M23.8298 8.05454C24.4245 6.12577 26.2211 4.72412 28.345 4.72412L31.4945 4.72412C33.6184 4.72412 35.415 6.12577 36.0097 8.05454M23.8298 8.05454C23.0411 8.10155 22.2548 8.15763 21.471 8.22268C19.0962 8.41979 17.322 10.4419 17.322 12.825V17.3219M17.322 17.3219H10.2357C8.9312 17.3219 7.87366 18.3794 7.87366 19.684L7.87366 43.3048C7.87366 44.6094 8.9312 45.6669 10.2357 45.6669H30.7071C32.0117 45.6669 33.0692 44.6094 33.0692 43.3048V19.684C33.0692 18.3794 32.0117 17.3219 30.7071 17.3219H17.322ZM14.1725 25.1955H14.1883V25.2113H14.1725V25.1955ZM14.1725 31.4944H14.1883V31.5101H14.1725V31.4944ZM14.1725 37.7933H14.1883V37.809H14.1725V37.7933Z"
      stroke="#00A06D"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconPillarProjectDark = ({ className, size = 40 }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M18.8967 25.1955H26.7703M18.8967 31.4944H26.7703M18.8967 37.7933H26.7703M33.0692 39.368H37.7934C40.4025 39.368 42.5175 37.2529 42.5175 34.6438V12.8249C42.5175 10.4419 40.7434 8.41979 38.3685 8.22268C37.5847 8.15763 36.7984 8.10155 36.0097 8.05454M23.8298 8.05454C23.694 8.49509 23.6209 8.96315 23.6209 9.44829C23.6209 10.318 24.3259 11.023 25.1956 11.023H34.6439C35.5136 11.023 36.2187 10.318 36.2187 9.44829C36.2187 8.96315 36.1455 8.49509 36.0097 8.05454M23.8298 8.05454C24.4245 6.12577 26.2211 4.72412 28.345 4.72412L31.4945 4.72412C33.6184 4.72412 35.415 6.12577 36.0097 8.05454M23.8298 8.05454C23.0411 8.10155 22.2548 8.15763 21.471 8.22268C19.0962 8.41979 17.322 10.4419 17.322 12.825V17.3219M17.322 17.3219H10.2357C8.9312 17.3219 7.87366 18.3794 7.87366 19.684L7.87366 43.3048C7.87366 44.6094 8.9312 45.6669 10.2357 45.6669H30.7071C32.0117 45.6669 33.0692 44.6094 33.0692 43.3048V19.684C33.0692 18.3794 32.0117 17.3219 30.7071 17.3219H17.322ZM14.1725 25.1955H14.1883V25.2113H14.1725V25.1955ZM14.1725 31.4944H14.1883V31.5101H14.1725V31.4944ZM14.1725 37.7933H14.1883V37.809H14.1725V37.7933Z"
      stroke="#CFBCFF"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconSearch = ({ className, size = 40 }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M42.8325 42.8325L31.0745 31.0746M34.9938 21.2763C34.9938 28.8523 28.8523 34.9939 21.2763 34.9939C13.7002 34.9939 7.55867 28.8523 7.55867 21.2763C7.55867 13.7003 13.7002 7.55872 21.2763 7.55872C28.8523 7.55872 34.9938 13.7003 34.9938 21.2763Z"
      stroke="white"
      strokeWidth="3.59937"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconGraduation = ({ className, size = 40 }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M10.1916 20.4564C9.66356 24.4959 9.34278 28.6006 9.24032 32.7595C14.8536 35.1031 20.1926 37.9727 25.1955 41.3067C30.1984 37.9727 35.5373 35.1031 41.1506 32.7595C41.0482 28.6006 40.7274 24.496 40.1993 20.4565M10.1916 20.4564C8.49425 19.8859 6.77628 19.3602 5.03912 18.8807C11.3297 14.4713 18.0769 10.669 25.1956 7.55872C32.3143 10.669 39.0614 14.4714 45.352 18.8807C43.6148 19.3602 41.8968 19.8859 40.1993 20.4565M10.1916 20.4564C15.4003 22.2072 20.4152 24.38 25.1956 26.9342C29.9759 24.38 34.9907 22.2073 40.1993 20.4565M15.0193 29.8637C15.8222 29.8637 16.4731 29.2129 16.4731 28.41C16.4731 27.6071 15.8222 26.9563 15.0193 26.9563C14.2165 26.9563 13.5656 27.6071 13.5656 28.41C13.5656 29.2129 14.2165 29.8637 15.0193 29.8637ZM15.0193 29.8637V22.7394C18.2979 20.7023 21.6942 18.8366 25.1955 17.1548M11.613 39.541C13.8839 37.2701 15.0193 34.2938 15.0193 31.3175V28.41"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconIdCard = ({ className, size = 40 }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M21.2762 15.3973H11.478C9.31339 15.3973 7.55865 17.152 7.55865 19.3166L7.55865 36.9535C7.55865 39.118 9.31339 40.8728 11.478 40.8728H38.9131C41.0777 40.8728 42.8324 39.118 42.8324 36.9535V19.3166C42.8324 17.152 41.0777 15.3973 38.9131 15.3973H29.1149M21.2762 15.3973V13.4376C21.2762 11.273 23.031 9.51831 25.1955 9.51831C27.3601 9.51831 29.1149 11.273 29.1149 13.4376V15.3973M21.2762 15.3973C21.2762 17.5618 23.031 19.3166 25.1955 19.3166C27.3601 19.3166 29.1149 17.5618 29.1149 15.3973M19.3166 31.0745C21.4812 31.0745 23.2359 29.3198 23.2359 27.1552C23.2359 24.9906 21.4812 23.2359 19.3166 23.2359C17.152 23.2359 15.3973 24.9906 15.3973 27.1552C15.3973 29.3198 17.152 31.0745 19.3166 31.0745ZM19.3166 31.0745C21.8763 31.0745 24.0539 32.7104 24.8609 34.9938M19.3166 31.0745C16.7568 31.0745 14.5791 32.7104 13.772 34.9938M31.0745 25.1955H36.9535M31.0745 33.0342H34.9938"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconHearts = ({ className, size = 40 }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M37.3631 12.7926C37.9733 18.5436 34.2886 23.9379 30.7972 27.7174C27.2584 31.5481 23.5196 34.1438 23.1221 34.4126L23.1232 34.4134C22.7522 34.6669 22.3233 34.8244 21.8765 34.8718C21.4298 34.9191 20.9782 34.8551 20.5624 34.6852L20.5623 34.6843C20.1132 34.5032 15.9145 32.7503 11.6528 29.7494C7.44584 26.787 2.71175 22.2862 2.10117 16.5353L2.10097 16.5334C1.80997 13.762 2.63024 10.9883 4.38169 8.82087C6.13315 6.65347 8.67289 5.26922 11.4436 4.97197L11.4455 4.97177L11.7655 4.94174C14.4057 4.73371 16.9152 5.43105 18.9068 6.89321C20.6133 4.97075 23.0423 3.74095 25.7995 3.44822L25.8014 3.44801C28.5728 3.15702 31.3465 3.97729 33.5139 5.72874C35.6813 7.4802 37.0656 10.0199 37.3629 12.7907L37.3631 12.7926Z"
      fill="#D22268"
      stroke="white"
      strokeWidth="3.125"
    />
    <path
      d="M48.0709 33.5576C46.8676 39.2143 41.6917 43.2 37.2003 45.7104C32.6481 48.2548 28.2886 49.563 27.8274 49.6953L27.8281 49.6965C27.3968 49.8224 26.9403 49.8391 26.5008 49.7456C26.0615 49.6521 25.6519 49.4512 25.3093 49.1607L25.3095 49.1598C24.9387 48.8484 21.4906 45.8798 18.3696 41.7053C15.2888 37.5843 12.1838 31.8372 13.3868 26.1805L13.3872 26.1786C13.97 23.4536 15.61 21.071 17.9473 19.5536C20.2845 18.0362 23.1283 17.5078 25.8546 18.0845L25.8565 18.0849L26.17 18.1555C28.7445 18.7766 30.9141 20.2177 32.3541 22.2255C34.5726 20.927 37.2632 20.5111 39.9753 21.0878L39.9772 21.0882C42.7022 21.671 45.0848 23.311 46.6022 25.6483C48.1196 27.9855 48.648 30.8294 48.0713 33.5556L48.0709 33.5576Z"
      fill="#D22268"
      stroke="white"
      strokeWidth="3.125"
    />
  </svg>
);

export const IconMessage = ({ className, size = 40 }: IconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 51 51"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M42.5175 17.8701C44.3737 18.4673 45.6669 20.2388 45.6669 22.2734V31.2715C45.6669 33.658 43.8877 35.6817 41.5091 35.8758C40.7974 35.9339 40.0837 35.9847 39.368 36.0281V42.5176L33.0691 36.2187C30.2268 36.2186 27.4119 36.1028 24.6291 35.8756C24.0027 35.8245 23.418 35.6465 22.8963 35.3684M42.5175 17.8701C42.1995 17.7678 41.8651 17.7 41.5179 17.6711C38.7322 17.4399 35.9144 17.322 33.069 17.322C30.2237 17.322 27.406 17.4399 24.6204 17.6711C22.2455 17.8682 20.4713 19.8903 20.4713 22.2734V31.2713C20.4713 33.0294 21.4369 34.5905 22.8963 35.3684M42.5175 17.8701V13.936C42.5175 10.5316 40.0992 7.58108 36.7232 7.14281C32.4347 6.58609 28.0616 6.29895 23.6214 6.29895C19.1808 6.29895 14.8072 6.58615 10.5183 7.14298C7.14233 7.58129 4.72412 10.5318 4.72412 13.9361V27.0068C4.72412 30.4112 7.14234 33.3617 10.5184 33.8C11.7298 33.9572 12.9479 34.093 14.1725 34.2069V44.0923L22.8963 35.3684"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const IconFingerprint = (props: IconProps) => (
  <IconBase {...props}>
    <path d="M12 11a5 5 0 0 1 5 5v3" />
    <path d="M7 13v3a5 5 0 0 0 10 0v-1" />
    <path d="M5 11a7 7 0 0 1 14 0" />
    <path d="M9 11v5" />
  </IconBase>
);

export const IconThumbUp = (props: IconProps) => (
  <IconBase {...props}>
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9" />
    <path d="M7 11H4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h9a2 2 0 0 0 2-1.6l1-5a2 2 0 0 0-2-2.4h-3" />
  </IconBase>
);

export const IconThumbDown = (props: IconProps) => (
  <IconBase {...props}>
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9" />
    <path d="M17 13h3a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-9a2 2 0 0 0-2 1.6l-1 5a2 2 0 0 0 2 2.4h3" />
  </IconBase>
);

export const IconHandshake = (props: IconProps) => (
  <IconBase {...props}>
    <path d="M4 12l4-4a2 2 0 0 1 2.83 0l1.17 1.17" />
    <path d="M20 12l-4 4a2 2 0 0 1-2.83 0l-1.17-1.17" />
    <path d="M8 12l4 4" />
    <path d="M12 8l4 4" />
  </IconBase>
);

export const IconPlay = (props: IconProps) => (
  <IconBase {...props} fill="currentColor" strokeWidth={0}>
    <polygon points="9 7 19 12 9 17 9 7" />
  </IconBase>
);

export const IconSpark = (props: IconProps) => <IconStar {...props} />;

export const IconVideo = (props: IconProps) => (
  <IconBase {...props}>
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    <polygon points="23 7 16 12 23 17 23 7" />
  </IconBase>
);
